import { contains } from '../../../utils/string';
import { pagesModel } from '../../../features/pages';
import { foldersModel } from '../../../features/folders';

import { FC, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SearchModalEmpty } from './SearchModalEmpty';
import { SearchModalNotFound } from './SearchModalNotFound';
import { Folder, Page } from '@distribute/shared/types';
import { SearchModalLabel } from './SearchModalLabel';
import { SearchModalContentItem } from './SearchModalContentItem';

import { redirectActions } from '../../../entities/history';
import { PageCardRowPreview } from '../../../entities/pages';

type Props = {
  folderId?: string;
  search: string;
  onClear: () => void;
};

export const SearchModalPages: FC<Props> = ({ folderId, search, onClear }) => {
  const pages = useSelector(pagesModel.selectors.selectPages);
  const folders = useSelector(foldersModel.selectors.selectFolders);

  const filteredPages = useMemo(() => {
    if (folderId) {
      return pages
        .filter((page) => page.folderId === folderId)
        .filter((p) => contains(p.content?.title || 'Untitled', search));
    }

    return pages.filter((p) =>
      contains(p.content?.title || 'Untitled', search)
    );
  }, [folderId, pages, search]);

  const foldersMap = useMemo(() => {
    return folders.reduce((acc, folder) => {
      acc[folder.id] = folder;
      return acc;
    }, {} as Record<string, Folder>);
  }, [folders]);

  const pagesGroupedByFolder = useMemo(() => {
    const grouped = {} as Record<string, Page[]>;

    for (const page of filteredPages) {
      if (!grouped[page.folderId]) {
        grouped[page.folderId] = [];
      }
      grouped[page.folderId].push(page);
    }

    return Object.entries(grouped).map(([folderId, pages]) => {
      const folderData = foldersMap[folderId] ?? {};
      return {
        ...folderData,
        pages,
      };
    });
  }, [filteredPages, foldersMap]);

  if (search.length === 0) {
    return <SearchModalEmpty />;
  }

  if (filteredPages.length === 0) {
    return <SearchModalNotFound onClear={onClear} name="pages" />;
  }

  return (
    <Content
      isCurrentFolder={!!folderId}
      pagesGroupedByFolder={pagesGroupedByFolder}
    />
  );
};

const Content = ({
  isCurrentFolder,
  pagesGroupedByFolder,
}: {
  isCurrentFolder: boolean;
  pagesGroupedByFolder: (Folder & { pages: Page[] })[];
}) => {
  const dispatch = useDispatch();

  return (
    <div className="flex flex-col gap-y-4">
      {pagesGroupedByFolder.map((folder) => (
        <div key={folder.id} className="flex flex-col gap-y-2">
          {!isCurrentFolder && (
            <SearchModalLabel>{folder?.title ?? 'Untitled'}</SearchModalLabel>
          )}
          {folder.pages.map((page) => (
            <ContentItem
              key={page.id}
              page={page}
              onClick={() =>
                dispatch(
                  redirectActions.toEditorPage({
                    sequenceNumber: page.sequenceNumber,
                  })
                )
              }
            />
          ))}
        </div>
      ))}
    </div>
  );
};

const ContentItem = ({
  page,
  onClick,
}: {
  page: Page;
  onClick: () => void;
}) => {
  return (
    <SearchModalContentItem onClick={onClick}>
      <PageCardRowPreview page={page} />
    </SearchModalContentItem>
  );
};
